import { fixedUi } from '@/i18n/fixedUi';
/**
 * TikTok 店铺基本信息解析
 * 来源：Get Authorized Shops / Active Shops 同步写入的 profile 缓存 + 主库授权字段
 */

const AUTH_STATUS_LABELS = {
  CONNECTED: '已授权',
  connected: '已授权',
  EXPIRED: '授权过期',
  expired: '授权过期',
  PENDING: '待授权',
  pending: '待授权',
};

const FIELD_LABELS = {
  mallName: '店铺名称',
  storeName: '店铺名称',
  mallId: '店铺 ID',
  shopId: '店铺 ID',
  supplierId: '商家 ID',
  uniqueId: '店铺 Cipher',
  shopCipher: '店铺 Cipher',
  openId: 'Open ID',
  shopTrackLabel: '店铺类型',
  shopType: '店铺类型代码',
  tiktokRegion: '店铺地区',
  region: '店铺地区',
  authStatus: '授权状态',
  expiredTime: '授权过期时间',
  tokenExpiresAt: '授权过期时间',
};

/** 基本信息展示字段：店铺 ID 与 Shop Cipher 一并展示 */
const STORE_INFO_FIELD_ORDER = [
  'mallName',
  'mallId',
  'shopCipher',
  'shopTrackLabel',
  'authStatus',
  'expiredTime',
];

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** 兼容后端已 unwrap 的 info，以及仍带 info 外层的原始响应 */
export function unwrapStoreInfoPayload(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.info && isPlainObject(raw.info)) return raw.info;
  return raw;
}

function formatExpiredTime(value) {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '—';
    // ISO 字符串（后端 resolveTokenExpiresAt）
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed) || trimmed.includes('T')) {
      const date = new Date(trimmed);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleString('zh-CN', { hour12: false });
      }
    }
    const asNum = Number(trimmed);
    if (Number.isFinite(asNum) && asNum > 0) {
      return formatExpiredTime(asNum);
    }
    return trimmed;
  }
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num) || num <= 0) return '—';
  const ms = num > 1e12 ? num : num * 1000;
  try {
    return new Date(ms).toLocaleString('zh-CN', { hour12: false });
  } catch {
    return String(value);
  }
}

function expiredEpochMs(value) {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed) || trimmed.includes('T')) {
      const ms = new Date(trimmed).getTime();
      return Number.isNaN(ms) ? null : ms;
    }
    const asNum = Number(trimmed);
    if (Number.isFinite(asNum) && asNum > 0) {
      return asNum > 1e12 ? asNum : asNum * 1000;
    }
    return null;
  }
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  return num > 1e12 ? num : num * 1000;
}

function isExpiredEpoch(value) {
  const ms = expiredEpochMs(value);
  return ms != null && ms <= Date.now();
}

export function formatStoreInfoValue(key, value) {
  if (value === undefined || value === null || value === '') return '—';

  if (key === 'authStatus') {
    const raw = AUTH_STATUS_LABELS[value] ?? AUTH_STATUS_LABELS[String(value)] ?? String(value);
    return fixedUi(raw);
  }

  if (key === 'expiredTime' || key === 'tokenExpiresAt') {
    return formatExpiredTime(value);
  }

  if (key === 'mallId' || key === 'supplierId') {
    return String(value);
  }

  if (typeof value === 'boolean') {
    return value ? fixedUi("是") : fixedUi("否");
  }

  return String(value);
}

function pickSectionRows(source, fieldOrder) {
  return fieldOrder.map((key) => ({
    key,
    label: fixedUi(FIELD_LABELS[key] || key),
    value: formatStoreInfoValue(key, isPlainObject(source) ? source[key] : undefined),
  }));
}

function readNested(payload, key) {
  if (!payload) return null;
  if (isPlainObject(payload[key])) return payload[key];
  return null;
}

/** 取出店铺基本信息对象 */
export function pickStoreInfoNode(raw) {
  const payload = unwrapStoreInfoPayload(raw);
  if (!payload) return null;
  const nested = readNested(payload, 'storeInfo');
  if (nested) return nested;
  if (
    Object.prototype.hasOwnProperty.call(payload, 'mallName')
    || Object.prototype.hasOwnProperty.call(payload, 'storeName')
    || Object.prototype.hasOwnProperty.call(payload, 'mallId')
    || Object.prototype.hasOwnProperty.call(payload, 'supplierId')
    || Object.prototype.hasOwnProperty.call(payload, 'authStatus')
    || Object.prototype.hasOwnProperty.call(payload, 'shopCipher')
  ) {
    return payload;
  }
  return null;
}

/** TikTok 无发品额度；保留空实现以免旧调用报错 */
export function pickStoreQuotaNode(_raw) {
  return null;
}

export function getStoreStatusMeta(raw) {
  const node = pickStoreInfoNode(raw);
  if (node && isExpiredEpoch(node.expiredTime ?? node.tokenExpiresAt)) {
    return { code: 'expired', label: fixedUi('授权过期'), modifier: 'failed' };
  }
  const auth = node?.authStatus;
  if (auth === undefined || auth === null || auth === '') {
    return { code: null, label: '—', modifier: 'offline' };
  }
  const label = formatStoreInfoValue('authStatus', auth);
  const key = String(auth).toUpperCase();
  if (key === 'CONNECTED') return { code: auth, label, modifier: 'success' };
  if (key === 'EXPIRED') return { code: auth, label, modifier: 'failed' };
  return { code: auth, label, modifier: 'pending' };
}

export function getStoreDisplayName(raw, fallback = '') {
  const node = pickStoreInfoNode(raw);
  const name = node?.mallName || node?.storeName;
  if (name !== undefined && name !== null && String(name).trim() !== '') {
    return String(name).trim();
  }
  return fallback || '—';
}

export function getStoreSupplierId(raw) {
  const node = pickStoreInfoNode(raw);
  const id = node?.mallId ?? node?.supplierId;
  if (id === undefined || id === null || id === '') return '';
  return String(id);
}

export function buildStoreBasicRows(raw) {
  const node = pickStoreInfoNode(raw);
  if (!node) return pickSectionRows(null, STORE_INFO_FIELD_ORDER);
  const source = { ...node };
  if (!source.mallName && source.storeName) source.mallName = source.storeName;
  if (!source.mallId && source.shopId) source.mallId = source.shopId;
  if (!source.mallId && source.supplierId) source.mallId = source.supplierId;
  if (!source.shopCipher && source.uniqueId) source.shopCipher = source.uniqueId;
  if (!source.shopTrackLabel && source.shopType) {
    const t = String(source.shopType).trim();
    if (t === '跨境' || t === '本土' || t === '全托管') source.shopTrackLabel = t;
  }
  if (!source.expiredTime && source.tokenExpiresAt) {
    source.expiredTime = source.tokenExpiresAt;
  }
  if (isExpiredEpoch(source.expiredTime)) {
    source.authStatus = 'expired';
  }
  return pickSectionRows(source, STORE_INFO_FIELD_ORDER);
}

export function buildStoreQuotaRows(_raw) {
  return [];
}

export function buildStoreInfoSections(raw) {
  const payload = unwrapStoreInfoPayload(raw);
  if (!payload) return [];

  const sections = [];
  const basicRows = buildStoreBasicRows(payload);
  if (basicRows.some((row) => row.value !== '—') || pickStoreInfoNode(payload)) {
    sections.push({ id: 'storeInfo', title: fixedUi('基本信息'), rows: basicRows });
  }
  return sections;
}
